import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  Collapse,
  Box,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from "material-react-table";
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { getDefaultMRTOptions } from "../../dedicatedDelivery/DefaultMrtOptions";
import { toast } from "react-toastify";
import { getFdpListByClient } from "../../../app/features/fdp/fdpSlice";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { Client } from "../../../app/models/responseModels/clientsResponse";
import { ClientAgent } from "../../../app/api/clients/ClientAgent";
import { 
  AddClientThirdPartyVendorRequest, 
  UpdateClientThirdPartyVendorRequest 
} from "../../../app/models/requestModels/clientRequest";
import { ApiResponse } from "../../../app/models/ApiResponse";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { formatDate, formatDateString } from "../../../components/common/utils/DateUtils";

// Define the interface for FDP data
interface Fdp {
  id: number;
  companyName: string;
}

// Updated interface to match the JSON response structure
export interface ClientThirdPartyVendors {
  id: number;
  clientId: number;
  fdpIds: number[];
  associatedFdpNames: string[];
  vendorName: string;
  description?: string;
  invoiceFrequency?: string;
  firstInvoicedDate?: string | Date;
  totalChargeableUnits?: number;
  pricePerUnit?: number;
  createdOn?: string;
  updatedOn?: string;
  createdBy?: string;
  updatedBy?: string;
}

interface ClientThirdPartyVendorsProps {
  clientData: Client;
}

const ClientThirdPartyVendors: React.FC<ClientThirdPartyVendorsProps> = ({ clientData }) => {
  const fdps = useSelector((state: RootState) => state.fdp.fdpListByClient);
  const [isLoading, setIsLoading] = useState(false);

  // State for form inputs
  const [selectedFdps, setSelectedFdps] = useState<number[]>([]);
  const [vendorName, setVendorName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [descriptionError, setDescriptionError] = useState<string>("");
  const [invoiceFrequency, setInvoiceFrequency] = useState<string>("");
  const [firstInvoiceDate, setFirstInvoiceDate] = useState<Date | null>(null);
  const [totalChargeableUnits, setTotalChargeableUnits] = useState<number | "">("");
  const [pricePerUnit, setPricePerUnit] = useState<number | "">("");
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Error states
  const [totalChargeableUnitsError, setTotalChargeableUnitsError] = useState<string>("");
  const [pricePerUnitError, setPricePerUnitError] = useState<string>("");

  // State for third-party vendors
  const [thirdPartyVendors, setThirdPartyVendors] = useState<ClientThirdPartyVendors[]>([]);

  const defaultMRTOptions = getDefaultMRTOptions<ClientThirdPartyVendors>();
  const dispatch = useAppDispatch();

  // Invoice Frequency Options
  const invoiceFrequencyOptions = [
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'BIWEEKLY', label: 'Biweekly' }, // ✅ New option added
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'ANNUALLY', label: 'Annually' },
  ];
  

  // Compute available FDPs (not already in use)
  const availableFdps = useMemo(() => {
    // Filter out FDPs that are either:
    // 1. Not used by any vendor
    // 2. Currently used by the vendor being edited
    const usedFdpIds = new Set(
      thirdPartyVendors
        .filter(vendor => vendor.id !== editId)
        .flatMap(vendor => vendor.fdpIds)
    );

    return fdps;
  }, [fdps, thirdPartyVendors, editId]);

  // Function to fetch FDP list for the client
  const fetchFdpList = () => {
    const clientId = Number(clientData.id);
    if (!isNaN(clientId)) {
      dispatch(getFdpListByClient(clientId));
    }
  };
  const frequencyMap: { [key: string]: string } = {
    "7": "WEEKLY",
    "14": "BIWEEKLY",
    "30": "MONTHLY",
    "90": "QUARTERLY",
    "365": "ANNUALLY",
  };

  // Function to fetch client third-party vendors
  const fetchClientThirdPartyVendors = async () => {
    const clientId = Number(clientData.id);
    if (isNaN(clientId)) return;

    setIsLoading(true);
    try {
      const response = await ClientAgent.getClientById(clientId);

      if (response.data.clientThirdPartyVendor && Array.isArray(response.data.clientThirdPartyVendor)) {
        const updatedVendors = response.data.clientThirdPartyVendor.map(vendor => ({
          id: vendor.id,
          clientId: vendor.clientId,
          fdpIds: vendor.fdpIds,
          associatedFdpNames: vendor.associatedFdpNames,
          vendorName: vendor.vendorName,
          description: vendor.description,
          invoiceFrequency: frequencyMap[vendor.invoiceFrequency?.toString() || ""] || vendor.invoiceFrequency,
          firstInvoicedDate: vendor.firstInvoicedDate,
          totalChargeableUnits: vendor.totalChargeableUnits,
          pricePerUnit: vendor.pricePerUnit,
        }));
        setThirdPartyVendors(updatedVendors);
      }
    } catch (error) {
      console.error("Error fetching client data:", error);
      toast.error("Failed to fetch client data");
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data loading
  useEffect(() => {
    fetchFdpList();
    fetchClientThirdPartyVendors();
  }, [dispatch, clientData.id]);

  // Clear form fields
  const clearForm = () => {
    setSelectedFdps([]);
    setVendorName("");
    setDescription("");
    setDescriptionError("");
    setInvoiceFrequency("");
    setFirstInvoiceDate(null);
    setTotalChargeableUnits("");
    setPricePerUnit("");
    setIsEditing(false);
    setEditId(null);
    setIsFormOpen(false);
    
    // Clear error states
    setTotalChargeableUnitsError("");
    setPricePerUnitError("");
  };

  // Validate description
  const validateDescription = (value: string): boolean => {
    if (value.trim() === "") {
      // Description is not mandatory, so empty is valid
      setDescriptionError("");
      return true;
    }

    // Count sentences (approximate by looking for periods followed by space or end of string)
    const sentenceMatches = value.match(/[.!?]+(?:\s|$)/g);
    const sentenceCount = sentenceMatches ? sentenceMatches.length : 0;

    if (sentenceCount > 5) {
      setDescriptionError("Description should not exceed 5 sentences according to industry standards");
      return false;
    }

    setDescriptionError("");
    return true;
  };

  // Handle description change
  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    validateDescription(value);
  };

  const handleSubmit = async () => {
    if (!vendorName || selectedFdps.length === 0 || 
        !invoiceFrequency || !firstInvoiceDate || 
        totalChargeableUnits === "" || pricePerUnit === "" ||
        totalChargeableUnitsError || pricePerUnitError || descriptionError) {
      toast.error("Please fill all required fields correctly");
      return;
    }
  
    setIsLoading(true);
  
    try {
      const clientId = Number(clientData?.id ?? 0);
  
      let response: ApiResponse;
  
      if (isEditing && editId !== null) {
        const updateRequest: UpdateClientThirdPartyVendorRequest = {
          id: editId,
          fdpIds: selectedFdps,
          clientId,
          vendorName,
          description,
          invoiceFrequency,
          firstInvoicedDate: formatDate(firstInvoiceDate),
          totalChargeableUnits: totalChargeableUnits as number,
          pricePerUnit: pricePerUnit as number,
        };
  
        response = await ClientAgent.updateClientThirdPartyVendor(updateRequest);
        toast.success("Third Party Vendor updated successfully");
      } else {
        const addRequest: AddClientThirdPartyVendorRequest = {
          clientId,
          fdpIds: selectedFdps,
          vendorName,
          description,
          invoiceFrequency,
          firstInvoicedDate: formatDate(firstInvoiceDate),
          totalChargeableUnits: totalChargeableUnits as number,
          pricePerUnit: pricePerUnit as number,
        };
        
        response = await ClientAgent.addClientThirdPartyVendor(addRequest);
        toast.success("Third Party Vendor added successfully");
      }
  
      await fetchClientThirdPartyVendors();
      clearForm();
    } catch (error) {
      console.error("Error submitting third party vendor:", error);
      toast.error("An error occurred while processing your request");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit action
  const handleEdit = (vendor: ClientThirdPartyVendors) => {
    setVendorName(vendor.vendorName);
    setDescription(vendor.description || "");
    validateDescription(vendor.description || "");
    setInvoiceFrequency(vendor.invoiceFrequency || "");
    setFirstInvoiceDate(vendor.firstInvoicedDate ? new Date(vendor.firstInvoicedDate + 'T00:00:00') : null);
    setTotalChargeableUnits(vendor.totalChargeableUnits || "");
    setPricePerUnit(vendor.pricePerUnit || "");
    
    // When editing, include the current vendor's FDPs in the selection
    const currentVendorFdpIds = vendor.fdpIds || [];
    setSelectedFdps(currentVendorFdpIds);
  
    setIsEditing(true);
    setEditId(vendor.id);
    setIsFormOpen(true);
    
    // Reset error states
    setTotalChargeableUnitsError("");
    setPricePerUnitError("");
  };

  // Handle delete action
  const handleDelete = async (id: number) => {
    try {
      setIsLoading(true);
      const response = await ClientAgent.deleteClientThirdPartyVendor(id);

      if (response) {
        toast.success("Third Party Vendor deleted successfully");
        await fetchClientThirdPartyVendors();
      } else {
        toast.error("Failed to delete third party vendor");
      }
    } catch (error) {
      console.error("Error deleting third party vendor:", error);
      toast.error("An error occurred while deleting the vendor");
    } finally {
      setIsLoading(false);
    }
  };

  // Modify input handlers for totalChargeableUnits and pricePerUnit
  const handleTotalChargeableUnitsChange = (value: string) => {
    const numValue = value ? Number(value) : "";
    
    if (numValue !== "" && numValue < 0) {
      setTotalChargeableUnitsError("Total chargeable units cannot be negative");
      setTotalChargeableUnits("");
    } else {
      setTotalChargeableUnitsError("");
      setTotalChargeableUnits(numValue);
    }
  };

  const handlePricePerUnitChange = (value: string) => {
    const numValue = value ? Number(value) : "";
    
    if (numValue !== "" && numValue < 0) {
      setPricePerUnitError("Price per unit cannot be negative");
      setPricePerUnit("");
    } else {
      setPricePerUnitError("");
      setPricePerUnit(numValue);
    }
  };

  // Define table columns
  const columns = useMemo<MRT_ColumnDef<ClientThirdPartyVendors>[]>(
    () => [
      {
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: "8px" }}>
            <IconButton
              color="primary"
              onClick={() => handleEdit(row.original)}
              size="small"
              disabled={isLoading}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="error"
              onClick={() => handleDelete(row.original.id)}
              size="small"
              disabled={isLoading}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ),
      },
      {
        accessorKey: "vendorName",
        header: "Vendor Name",
      },
      {
        accessorKey: "associatedFdpNames",
        header: "Associated FDPs",
        Cell: ({ cell }) => {
          const value = cell.getValue() as string[];
          return <span>{value?.join(", ") || "N/A"}</span>;
        },
      },
      {
        accessorKey: "invoiceFrequency",
        header: "Invoice Frequency",
        Cell: ({ cell }) => <span>{String(cell.getValue() ?? "N/A")}</span>,
      },
      {
        accessorKey: "firstInvoicedDate",
        header: "First Invoice Date",
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatDateString(value);
          return formattedDate;
        },
      },
      {
        accessorKey: "totalChargeableUnits",
        header: "Total Chargeable Units",
        Cell: ({ cell }) => <span>{(cell.getValue() as number) ?? 0}</span>,
      },
      {
        accessorKey: "pricePerUnit",
        header: "Price Per Unit",
        Cell: ({ cell }) => {
          const value = cell.getValue() as number | null;
          return <span>{value !== null ? `$${value.toFixed(2)}` : "N/A"}</span>;
        },
      },
    ],
    [isLoading]
  );

  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns,
    data: thirdPartyVendors,
    initialState: {
      ...defaultMRTOptions.initialState,
      sorting: [{ id: "vendorName", desc: false }],
    },
    state: {
      showProgressBars: isLoading,
    },
    enableRowActions: false,
    muiTableHeadProps: {
      sx: {
        "& .MuiTableCell-root": {
          backgroundColor: "grey.300",
          color: "grey.800",
          fontWeight: "bold",
        },
      },
    },
  });

  // Modify the submit button disable logic - description is not mandatory
  const isSubmitDisabled = 
    isLoading ||
    !vendorName ||
    selectedFdps.length === 0 ||
    !invoiceFrequency ||
    !firstInvoiceDate ||
    totalChargeableUnits === "" ||
    pricePerUnit === "" ||
    !!totalChargeableUnitsError ||
    !!pricePerUnitError ||
    !!descriptionError;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Card>
        <CardHeader
          subheader="Third Party Vendors"
          action={
            <IconButton
              color="primary"
              onClick={() => setIsFormOpen(!isFormOpen)}
              size="small"
              disabled={isLoading}
            >
              {isFormOpen ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
            </IconButton>
          }
        />
        <CardContent>
          <Collapse in={isFormOpen}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {isEditing ? "Edit Third Party Vendor" : "Add New Third Party Vendor"}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      margin="normal"
                      id="vendorName"
                      label="Vendor Name"
                      fullWidth
                      size="small"
                      value={vendorName}
                      onChange={(e) => setVendorName(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      margin="normal"
                      id="associatedFdpIds"
                      label="Associated FDPs"
                      fullWidth
                      size="small"
                      value={selectedFdps}
                      onChange={(e) =>
                        setSelectedFdps(
                          typeof e.target.value === "string"
                            ? e.target.value.split(",").map(Number)
                            : e.target.value
                        )
                      }
                      disabled={isLoading || availableFdps.length === 0}
                      SelectProps={{
                        multiple: true,
                        renderValue: (selected: any) =>
                          (selected as number[])
                            .map((id) => availableFdps.find((fdp: Fdp) => fdp.id === id)?.companyName || 
                                     thirdPartyVendors.find(v => v.fdpIds.includes(id))?.associatedFdpNames[0])
                            .join(", "),
                      }}
                      required
                    >
                      {availableFdps.length === 0 && thirdPartyVendors.length > 0 ? (
                        thirdPartyVendors
                          .find(v => v.id === editId)
                          ?.fdpIds.map((fdpId) => {
                            const fdp = fdps.find((f: Fdp) => f.id === fdpId);
                            return (
                              <MenuItem key={fdpId} value={fdpId}>
                                {fdp?.companyName}
                              </MenuItem>
                            );
                          })
                      ) : (
                        availableFdps.map((fdp: Fdp) => (
                          <MenuItem key={fdp.id} value={fdp.id}>
                            {fdp.companyName}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      margin="normal"
                      id="description"
                      label="Description"
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      value={description}
                      onChange={(e) => handleDescriptionChange(e.target.value)}
                      disabled={isLoading}
                      error={!!descriptionError}
                      helperText={descriptionError}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      margin="normal"
                      id="invoiceFrequency"
                      label="Invoice Frequency"
                      fullWidth
                      size="small"
                      value={invoiceFrequency}
                      onChange={(e) => setInvoiceFrequency(e.target.value)}
                      disabled={isLoading}
                      required
                    >
                      {invoiceFrequencyOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="First Invoice Date *"
                      value={firstInvoiceDate}
                      onChange={(newValue) => setFirstInvoiceDate(newValue)}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true, 
                          margin: 'normal', 
                          size: 'small',
                          required: true
                        } 
                      }}
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      margin="normal"
                      id="totalChargeableUnits"
                      label="Total Chargeable Units"
                      fullWidth
                      size="small"
                      type="number"
                      value={totalChargeableUnits}
                      onChange={(e) => handleTotalChargeableUnitsChange(e.target.value)}
                      inputProps={{ min: 0 }}
                      disabled={isLoading}
                      error={!!totalChargeableUnitsError}
                      helperText={totalChargeableUnitsError}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      margin="normal"
                      id="pricePerUnit"
                      label="Price Per Unit"
                      fullWidth
                      size="small"
                      type="number"
                      value={pricePerUnit}
                      onChange={(e) => handlePricePerUnitChange(e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      disabled={isLoading}
                      error={!!pricePerUnitError}
                      helperText={pricePerUnitError}
                      required
                    />
                  </Grid>
                </Grid>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleSubmit}
                    sx={{ mr: 2 }}
                    disabled={isSubmitDisabled}
                  >
                    {isEditing ? "Update" : "Add"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={clearForm}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Collapse>

          <MaterialReactTable table={table} />
        </CardContent>
      </Card>
    </LocalizationProvider>
  );
};

export default ClientThirdPartyVendors;